import { useState } from "react";
import { assetUrl, errorMessage } from "../api/client";
import {
  suggestionErrorLabel,
  useDismissSuggestion,
  useRestoreSuggestion,
} from "../api/clipSuggestions";
import type { ClipSuggestionList, ClipSuggestionRead } from "../api/types";
import { categoryColor } from "../lib/grade";

// The AI clip suggestions under the trim track: one card per proposal with its frames, task,
// confidence and the clip-rule checklist the verify pass answered. "Add to composer" turns
// a proposal into a normal editable range (the reviewer still trims + confirms); dismissed
// ones collapse into a group with Restore. The reviewer decides — a failed check only greys
// a card and says why.

interface Props {
  recordingId: number;
  durationMs: number;
  data: ClipSuggestionList | undefined;
  loading: boolean;
  minMs: number;
  /** suggestion id -> composer clip number (1-based) for proposals already added */
  added: Map<number, number>;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onSeek: (ms: number) => void;
  /** Add a proposal to the composer; returns false when no free room is left for it. */
  onAdd: (s: ClipSuggestionRead, extendToMin: boolean) => boolean;
  onAddAll: () => void;
}

export function fmtMs(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function Check({ ok, label, warn }: { ok: boolean | null; label: string; warn?: boolean }) {
  if (ok == null) return null;
  const cls = ok ? "chk ok" : warn ? "chk warn" : "chk bad";
  return (
    <span className={cls}>
      {ok ? "✓" : warn ? "⚠" : "✗"} {label}
    </span>
  );
}

function Checklist({ s, minMs }: { s: ClipSuggestionRead; minMs: number }) {
  const c = s.checks;
  const longGaps = c.idle_gaps.filter((g) => g.end_ms - g.start_ms > 10_000);
  return (
    <div className="checks">
      <Check ok={c.single_activity} label="one activity" />
      <Check ok={c.start_captured} label="start captured" />
      <Check ok={c.end_captured} label="end captured" />
      {c.edits_or_speed_changes != null && (
        <Check ok={!c.edits_or_speed_changes} label="no edits or speed changes" />
      )}
      {c.verified && longGaps.length === 0 && (
        <span className="chk ok">✓ no idle gap over 10 s</span>
      )}
      {longGaps.map((g, i) => (
        <span key={i} className="chk warn" title="The clip rules forbid idle gaps over 10 s">
          ⚠ idle {fmtMs(g.end_ms - g.start_ms)} at {fmtMs(g.start_ms)} · check
        </span>
      ))}
      {s.too_short && <span className="chk bad">✗ shorter than {fmtMs(minMs)}</span>}
      {!c.verified && !s.too_short && (
        <span className="chk muted" title="The verify pass did not run for this window (budget)">
          not verified
        </span>
      )}
    </div>
  );
}

export default function ClipSuggestionsPanel({
  recordingId,
  durationMs,
  data,
  loading,
  minMs,
  added,
  selectedId,
  onSelect,
  onSeek,
  onAdd,
  onAddAll,
}: Props) {
  const dismiss = useDismissSuggestion(recordingId);
  const restore = useRestoreSuggestion(recordingId);
  const [noRoom, setNoRoom] = useState<number | null>(null);

  if (loading) return <p className="muted small">Loading AI suggestions…</p>;
  const reco = data?.recommendation ?? null;
  if (!reco) return null; // the stage never ran for this recording (older run / disabled)

  const items = data?.items ?? [];
  const proposed = items.filter((s) => s.status === "proposed");
  const dismissed = items.filter((s) => s.status === "dismissed");
  const accepted = items.filter((s) => s.status === "accepted");
  const eligible = proposed.filter((s) => !s.too_short && !added.has(s.id));
  const coveredMs = proposed.reduce((a, s) => a + s.duration_ms, 0);

  if (reco.status !== "available" || items.length === 0) {
    const why = suggestionErrorLabel(reco.error);
    return (
      <div className="sugg-empty">
        <span className="sugg-star">✦</span>
        <span>
          <b>No AI suggestions.</b>{" "}
          {why ?? "The model found no window that could be delivered as a clip."}
        </span>
      </div>
    );
  }

  return (
    <div className="sugg-panel">
      <div className="sugg-head">
        <h4>
          <span className="sugg-star">✦</span> AI clip suggestions
        </h4>
        <span className="muted small sugg-meta">
          <b>{proposed.length} proposed</b>
          {accepted.length > 0 && ` · ${accepted.length} accepted`}
          {" · "}
          {fmtMs(coveredMs)} of {fmtMs(durationMs)} covered
          {reco.model && ` · ${reco.model.replace(/^.*\//, "")}`}
          {reco.calls > 0 && ` · ${reco.calls} calls`}
          {reco.llm_cost_usd != null && ` · $${reco.llm_cost_usd.toFixed(3)}`}
          {reco.truncated && " · partial (budget)"}
        </span>
        <button
          className="btn small"
          onClick={onAddAll}
          disabled={eligible.length === 0}
          title="Add every proposal that passes the length rule and fits on the timeline"
        >
          Add all eligible ({eligible.length})
        </button>
      </div>

      <div className="sugg-list">
        {proposed.map((s) => {
          const color = categoryColor(s.task_category_id);
          const clipNo = added.get(s.id);
          const cls = [
            "sugg",
            s.id === selectedId ? "selected" : "",
            clipNo ? "added" : "",
            s.too_short ? "short" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const play = (ms: number) => {
            onSelect(s.id);
            onSeek(ms);
          };
          return (
            <article key={s.id} className={cls} onClick={() => play(s.start_ms)}>
              <div className="sugg-idx" style={{ ["--sc" as string]: color }}>
                {s.rank}
              </div>
              <div className="sugg-body">
                <div className="sugg-top">
                  <span className="sugg-win mono">
                    {fmtMs(s.start_ms)} → {fmtMs(s.end_ms)}
                  </span>
                  <span className={s.too_short ? "sugg-dur bad" : "sugg-dur"}>
                    {fmtMs(s.duration_ms)}
                    {s.too_short && ` · under ${fmtMs(minMs)}`}
                  </span>
                  {s.task_category && (
                    <span className="cat-badge" style={{ ["--sc" as string]: color }}>
                      {s.task_category}
                    </span>
                  )}
                  <span className="sugg-task">{s.task_title ?? "No catalog task matched"}</span>
                  {s.task_id && (
                    <span className={s.task_confidence >= 0.7 ? "sugg-conf" : "sugg-conf mid"}>
                      {Math.round(s.task_confidence * 100)}% task match
                    </span>
                  )}
                </div>
                {(s.thumbs.start || s.thumbs.mid || s.thumbs.end) && (
                  <div className="sugg-thumbs">
                    {(["start", "mid", "end"] as const).map((k) => {
                      const url = s.thumbs[k];
                      return url ? (
                        <img
                          key={k}
                          className="sugg-thumb"
                          src={assetUrl(url)}
                          alt={`${k} frame`}
                          loading="lazy"
                        />
                      ) : null;
                    })}
                  </div>
                )}
                {s.summary && <p className="sugg-summary">{s.summary}</p>}
                <Checklist s={s} minMs={minMs} />
                {s.checks.notes && <p className="sugg-notes">{s.checks.notes}</p>}
                <div className="sugg-foot">
                  <span className="sugg-bar" title={`confidence ${s.confidence.toFixed(2)}`}>
                    <i style={{ width: `${Math.round(s.confidence * 100)}%` }} />
                  </span>
                  <span className="muted small">confidence {s.confidence.toFixed(2)}</span>
                  {noRoom === s.id && (
                    <span className="small error-text">
                      no free room on the timeline for this window
                    </span>
                  )}
                  <span className="sugg-sp" />
                  {clipNo ? (
                    <span className="sugg-added">Added as clip {clipNo}</span>
                  ) : s.too_short ? (
                    <button
                      className="btn small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNoRoom(onAdd(s, true) ? null : s.id);
                      }}
                      title={`Add it stretched to ${fmtMs(minMs)} so it can be delivered`}
                    >
                      Extend to {fmtMs(minMs)} and add
                    </button>
                  ) : (
                    <button
                      className="btn small primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNoRoom(onAdd(s, false) ? null : s.id);
                      }}
                    >
                      Add to composer
                    </button>
                  )}
                  {s.checks.idle_gaps.length > 0 && (
                    <button
                      className="btn small"
                      onClick={(e) => {
                        e.stopPropagation();
                        play(s.checks.idle_gaps[0].start_ms);
                      }}
                    >
                      Play at {fmtMs(s.checks.idle_gaps[0].start_ms)}
                    </button>
                  )}
                  <button
                    className="btn small ghost"
                    disabled={dismiss.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      dismiss.mutate({ id: s.id });
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {(dismiss.error != null || restore.error != null) && (
        <p className="error">{errorMessage(dismiss.error ?? restore.error)}</p>
      )}

      {dismissed.length > 0 && (
        <details className="sugg-dismissed">
          <summary>Dismissed ({dismissed.length})</summary>
          {dismissed.map((s) => (
            <div key={s.id} className="sugg-dismissed-row">
              <span className="mono small">
                {fmtMs(s.start_ms)} → {fmtMs(s.end_ms)}
              </span>
              <span className="small">{s.task_title ?? "no task"}</span>
              {s.decided_by && <span className="muted small">by {s.decided_by}</span>}
              <span className="sugg-sp" />
              <button
                className="btn small ghost"
                disabled={restore.isPending}
                onClick={() => restore.mutate({ id: s.id })}
              >
                Restore
              </button>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
